/**
 * Hydrogen template renderer public API.
 *
 * DOM rendering, no-DOM string rendering, template compilation, and legacy
 * compatibility are implemented in cohesive sibling modules. This entry keeps
 * the original import path and public surface stable.
 */
import { rawHtml } from '../ui/html-escape.js';
import { instantiateTemplate, mountTemplate } from './render-dom.js';
import { hydrateInstance } from './render-hydrate.js';
import { registerTemplate, templateForStrings } from './render-template.js';
import { isTemplateInstanceValue, runtimeError } from './render-values.js';
import { setTrustedInnerHTML } from './trusted-html.js';
const domAdapter = {
    createTemplate(staticHtml, namespace) {
        const documentObj = globalThis.document;
        if (!documentObj?.createElement) {
            throw runtimeError('RT401', 'DOM rendering requires document.createElement');
        }
        const element = documentObj.createElement('template');
        // SVG (and other foreign content) only parses correctly under an actual
        // <svg> ancestor — the HTML parser's foreign-content rules kick in on
        // the open tag itself, even inside an inert <template>. The wrapper
        // element is stripped back out on the mount path (render-dom.ts) so it
        // never reaches the live DOM; string rendering never sees it at all.
        const markup = namespace === 'svg' ? `<svg>${staticHtml}</svg>` : staticHtml;
        setTrustedInnerHTML(element, markup);
        return element;
    },
    fragmentFromHtml(markup, documentObj) {
        const template = documentObj.createElement('template');
        setTrustedInnerHTML(template, markup);
        return Array.from(template.content.childNodes || []);
    },
    replaceChildren(container, nodesOrFragment) {
        // Native replaceChildren is variadic: a bare array is stringified.
        const nodes = Array.isArray(nodesOrFragment) ? nodesOrFragment : [nodesOrFragment];
        if (typeof container.replaceChildren === 'function') {
            container.replaceChildren(...nodes);
            return;
        }
        while (container.firstChild) {
            container.removeChild(container.firstChild);
        }
        for (const node of nodes) {
            container.appendChild(node);
        }
    },
};
export { cache, conditional, createRef, live, ref, repeat, spread, until, } from './render-values.js';
export { renderToString } from './render-string.js';
export { classMap, styleMap } from './render-class-style.js';
export { compileHtmlStrings } from './render-template.js';
/** Tagged template entrypoint for hand-written Hydrogen templates. */
export function html(strings, ...values) {
    return instantiateTemplate(templateForStrings(strings), values, domAdapter);
}
/**
 * Tagged template entrypoint for SVG fragments (G12). Compiles through the
 * same path as `html`, but the resulting template carries `namespace: 'svg'`
 * so DOM mounting parses the static markup as SVG foreign content instead of
 * ordinary HTML — required for elements like `<circle>`/`<path>` to gain
 * their real SVG behavior (e.g. `getBBox`) rather than becoming unknown
 * HTMLUnknownElements. String rendering is unaffected: no wrapper markup is
 * ever emitted.
 */
export function svg(strings, ...values) {
    return instantiateTemplate(templateForStrings(strings, 'svg'), values, domAdapter);
}
/**
 * Registers or returns a compiler-addressable template. `namespace` carries
 * the AOT-lowered `svg` tag's foreign-content namespace so its registration
 * parses and mounts exactly like the runtime `svg` tag's own compile (#1410).
 * `nav` carries the compiler's navigation program for a lowered template, so
 * its instances mount through the direct-materialization seam (#1584).
 */
export function tmpl(id, staticHtml, partSpecs = [], namespace, nav) {
    return registerTemplate(id, staticHtml, partSpecs, namespace, nav);
}
/** Creates an instance from a template and positional or named bindings. */
export function instantiate(template, bindings = []) {
    return instantiateTemplate(template, bindings, domAdapter);
}
/** Mounts a template instance or legacy component into a DOM element. */
export function mount(el, instance) {
    return mountTemplate(el, instance, domAdapter);
}
/**
 * Attaches explicit, dependency-aware effects to a template instance.
 *
 * Effects start after DOM parts and refs bind on mount or hydrate. Cleanup
 * runs before a changed dependency restarts the effect and when the instance
 * unmounts. Registration is declarative and survives remounting.
 */
export function withLifecycle(instance, options) {
    const lifecycleInstance = instance;
    if (typeof lifecycleInstance.registerLifecycleEffects !== 'function') {
        throw runtimeError('RT401', 'withLifecycle requires a Hydrogen template instance');
    }
    if (!options || !Array.isArray(options.effects)) {
        throw runtimeError('RT401', 'withLifecycle requires an effects array');
    }
    const effects = options.effects;
    for (const definition of effects) {
        if (typeof definition.setup !== 'function' ||
            !Array.isArray(definition.dependencies) ||
            definition.dependencies.some((dependency) => typeof dependency !== 'function')) {
            throw runtimeError('RT401', 'withLifecycle effects require a setup function and dependency-reader array');
        }
    }
    lifecycleInstance.registerLifecycleEffects(effects);
    return instance;
}
/**
 * Adopts server-rendered markup already inside `el` instead of replacing it
 * (#887).
 *
 * The markup must be the output of `renderToString`/`renderToStream` for this
 * same instance. Matching nodes keep their identity — so focus, selection,
 * scroll offset, and media state survive the handover — while event listeners,
 * DOM properties, and refs attach as they would on mount.
 *
 * A structural disagreement with the server markup is not an error: the
 * subtree falls back to a full client render, the runtime logger records why,
 * and the returned teardown behaves identically either way.
 */
export function hydrate(el, instance) {
    if (!el) {
        throw runtimeError('RT401', 'hydrate target missing');
    }
    if (isTemplateInstanceValue(instance)) {
        const result = hydrateInstance(instance, el);
        if (result)
            return result.unmount;
    }
    return mountTemplate(el, instance, domAdapter);
}
export { rawHtml };
export function isTemplateInstance(value) {
    return isTemplateInstanceValue(value);
}
//# sourceMappingURL=render.js.map